import React, { useEffect, useState } from "react";
import "./ProjectsPage.css";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const fetchAllProjects = async (apiToken) => {
  let allProjects = [];
  let page = 1;
  const pageSize = 200;
  let totalPages = 1;

  while (page <= totalPages) {
    const response = await fetch(
      `${API_BASE_URL}/api/single-projects?populate=GridImage&filters[IncludeProjectsPage][$eq]=true&pagination[page]=${page}&pagination[pageSize]=${pageSize}`,
      {
        headers: {
          Authorization: `Bearer ${apiToken}`,
        },
      }
    );
    const data = await response.json();
    allProjects = [...allProjects, ...data.data];
    totalPages = data.meta.pagination.pageCount;
    page++;
  }

  return allProjects
    .filter(
      (project) =>
        project.attributes.GridImage && project.attributes.GridImage.data
    )
    .sort((a, b) => {
      if (a.attributes.GridOrder === null) return 1;
      if (b.attributes.GridOrder === null) return -1;
      return a.attributes.GridOrder - b.attributes.GridOrder;
    });
};

const ProjectsPage = ({ apiToken, onNavigate, setIsHeaderVisible }) => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [loadedImages, setLoadedImages] = useState({});

  const {
    data: projects = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["projects-page"],
    queryFn: () => fetchAllProjects(apiToken),
    staleTime: 1000 * 60 * 60 * 24, // 24h
    cacheTime: 1000 * 60 * 60 * 24, // 24h em cache
  });

  useEffect(() => {
    const storedScrollPosition = sessionStorage.getItem(
      "projectsScrollPosition"
    );
    if (storedScrollPosition) {
      setScrollPosition(parseFloat(storedScrollPosition));
    }
  }, []);

  useEffect(() => {
    setIsHeaderVisible(true);
  }, [setIsHeaderVisible]);

  const handleLinkClick = (e, path) => {
    e.preventDefault();
    const pageElement = document.querySelector(".page");
    if (pageElement) {
      const currentScrollPosition = pageElement.scrollTop;
      sessionStorage.setItem("projectsScrollPosition", currentScrollPosition);
    }
    onNavigate(path);
  };

  useEffect(() => {
    const pageElement = document.querySelector(".page");
    if (pageElement && scrollPosition !== 0) {
      const timeoutId = setTimeout(() => {
        pageElement.scrollTo({ top: scrollPosition, behavior: "auto" });
        sessionStorage.removeItem("projectsScrollPosition");
      }, 150);
      return () => clearTimeout(timeoutId);
    }
  }, [scrollPosition]);

  const handleImageLoad = (id) => {
    setLoadedImages((prev) => ({ ...prev, [id]: true }));
  };

  if (isLoading) return <p></p>;
  if (error) return <p>Erro ao carregar lista de projetos.</p>;

  const ogImage = projects?.[0]?.attributes?.GridImage?.data?.attributes?.url || "https://productive-prosperity-58dbc90efe.media.strapiapp.com/SEINEDESIGN_hotel_flottant_paris_off_1_3296f3d699.jpg"
  const ogUrl = typeof window !== "undefined" ? window.location.href : "https://ronzatti.com/projects";

  return (
    <>
      <Helmet>
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={ogUrl} />
        <link rel="canonical" href={ogUrl} />
        <title>
          Projets d'Architecture Flottante - Réalisations Seine Design
        </title>
        <meta
          name="description"
          content="Découvrez les projets phares de Seine Design en architecture flottante : hôtels, galeries d'art et plus."
        />
        <meta
          property="og:title"
          content="Projets d'Architecture Flottante - Réalisations Seine Design"
        />
        <meta
          property="og:description"
          content="Explorez nos réalisations innovantes en architecture navale et flottante. Découvrez les projets phares de Seine Design, des hôtels aux galeries d'art sur l'eau, et laissez-vous inspirer."
        />
        <meta
          property="og:image"
          content={ogImage}
        />
      </Helmet>
      <div className="projects-grid">
        {projects.map((project, index) => {
          const imageUrl = project.attributes.GridImage.data.attributes.url;
          const isLoaded = loadedImages[project.id];
          const imageClass = isLoaded
            ? "project-image"
            : "project-image loading";

          const imageProps = {
            src: imageUrl,
            alt: project.attributes.Title,
            onLoad: () => handleImageLoad(project.id),
            className: imageClass,
            style: {
              objectPosition: project.attributes.ObjectPosition || "center",
            },
          };

          return (
            <div
              key={project.id}
              className={`project-item ${project.attributes.GridImageHeight} ${project.attributes.GridImageWidth}`}
              style={{ "--i": index + 1 }}
            >
              {project.attributes.HasSinglePage ? (
                <a
                  href={`/projects/${project.attributes.slug}`}
                  onClick={(e) =>
                    handleLinkClick(e, `/projects/${project.attributes.slug}`)
                  }
                >
                  <img {...imageProps} />
                </a>
              ) : (
                <img {...imageProps} />
              )}
              <div className="project-details">
                <h3 className="title">{project.attributes.Title}</h3>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default ProjectsPage;
